import { AuthScreenShell } from '@/components/auth/AuthScreenShell'
import { AUTH_COLORS, authScreenStyles as styles } from '@/components/auth/authScreenStyles'
import { BodyText, PageTitle } from '@/components/ui/Typography'
import { Ionicons } from '@expo/vector-icons'
import { useRouter } from 'expo-router'
import React, { useState } from 'react'
import {
  ActivityIndicator,
  Linking,
  Platform,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native'
import { URLS } from '@/lib/constants'
import { useAuth } from '../../lib/AuthContext'
import { supabase } from '../../lib/supabase'

export default function LoginScreen() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { signIn, signInWithGoogle, user } = useAuth()
  const router = useRouter()

  const handleLogin = async () => {
    if (!email || !password) {
      setError('Please enter email and password')
      return
    }
    setLoading(true)
    setError(null)
    try {
      const { error: signInError } = await signIn(email, password)
      if (signInError) {
        setError(signInError.message)
        setLoading(false)
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred')
      setLoading(false)
    }
  }

  const handleGoogleSignIn = async () => {
    setLoading(true)
    setError(null)
    try {
      const { error: googleError } = await signInWithGoogle()
      if (googleError) {
        setError(googleError.message)
        setLoading(false)
      } else if (Platform.OS !== 'web') {
        setTimeout(() => {
          if (user) {
            router.replace('/')
          } else {
            supabase.auth.getSession().then(({ data: { session } }) => {
              if (session) {
                router.replace('/')
              }
              setLoading(false)
            })
          }
        }, 500)
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred')
      setLoading(false)
    }
  }

  return (
    <AuthScreenShell>
      <View style={styles.card}>
        <PageTitle ta="center" fontWeight="700" color={AUTH_COLORS.text} mb={8}>
          Welcome back
        </PageTitle>
        <BodyText ta="center" color={AUTH_COLORS.muted} mb={28}>
          Sign in to continue learning
        </BodyText>

        {error && (
          <View style={styles.errorContainer} testID="error-container">
            <Text style={styles.errorText}>{error}</Text>
          </View>
        )}

        <View style={styles.form}>
          <View style={styles.inputContainer}>
            <Text style={styles.label}>Email</Text>
            <TextInput
              style={styles.input}
              placeholder="Enter your email"
              placeholderTextColor={AUTH_COLORS.muted}
              value={email}
              onChangeText={setEmail}
              keyboardType="email-address"
              autoCapitalize="none"
              autoCorrect={false}
              editable={!loading}
            />
          </View>

          <View style={styles.inputContainer}>
            <Text style={styles.label}>Password</Text>
            <View style={styles.passwordField}>
              <TextInput
                style={[styles.input, styles.passwordInput]}
                placeholder="Enter your password"
                placeholderTextColor={AUTH_COLORS.muted}
                value={password}
                onChangeText={setPassword}
                secureTextEntry={!showPassword}
                editable={!loading}
                autoCapitalize="none"
                autoCorrect={false}
              />
              <TouchableOpacity
                style={styles.passwordToggle}
                onPress={() => setShowPassword((v) => !v)}
                accessibilityRole="button"
                accessibilityLabel={showPassword ? 'Hide password' : 'Show password'}
                hitSlop={8}
                disabled={loading}
              >
                <Ionicons
                  name={showPassword ? 'eye-off-outline' : 'eye-outline'}
                  size={22}
                  color={AUTH_COLORS.muted}
                />
              </TouchableOpacity>
            </View>
          </View>

          {/* Password resets go through WordPress so both systems stay in
              sync: wp-login re-syncs the Supabase password on the next
              sign-in. An in-app Supabase-only reset would silently drift. */}
          <TouchableOpacity
            style={styles.forgotPassword}
            onPress={() => void Linking.openURL(URLS.LOST_PASSWORD)}
            disabled={loading}
          >
            <Text style={styles.link}>Forgot password?</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.button, styles.primaryButton]}
            accessibilityRole="button"
            onPress={handleLogin}
            disabled={loading}
          >
            {loading ? (
              <ActivityIndicator color="#fff" />
            ) : (
              <Text style={styles.buttonText}>Sign In</Text>
            )}
          </TouchableOpacity>

          {/* <View style={styles.divider}>
            <View style={styles.dividerLine} />
            <Text style={styles.dividerText}>or</Text>
            <View style={styles.dividerLine} />
          </View>

          <TouchableOpacity
            style={[styles.button, styles.googleButton]}
            onPress={handleGoogleSignIn}
            disabled={loading}
          >
            <Text style={styles.googleButtonText}>Continue with Google</Text>
          </TouchableOpacity> */}

          <View style={styles.footerRow}>
            <Text style={styles.linkMuted}>Don&apos;t have an account? </Text>
            <TouchableOpacity
              onPress={() => router.push('/auth/signup' as any)}
              disabled={loading}
            >
              <Text style={styles.link}>Sign Up</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </AuthScreenShell>
  )
}
